import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useUserCoupons, useAdminCoupons } from "@/hooks/queries";
import { couponsService } from "@/services/couponsService";

interface CouponsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (code: string, discountAmount: number) => void;
  orderAmount: number;
  storeId?: number;
}

export const CouponsDialog: React.FC<CouponsDialogProps> = ({
  open,
  onOpenChange,
  onApply,
  orderAmount,
  storeId,
}) => {
  const { toast } = useToast();
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const { data: userCoupons = [], isLoading: isLoadingCoupons } = useUserCoupons();
  const { data: platformCouponsData, isLoading: isLoadingPlatform } = useAdminCoupons({ isActive: true });
  const platformCoupons = platformCouponsData?.items ?? [];

  const handleApplyCoupon = async (codeToApply?: string) => {
    const code = (codeToApply || couponCodeInput).trim().toUpperCase();
    if (!code) return;

    setCouponError("");
    setIsValidatingCoupon(true);

    try {
      const response = await couponsService.validate({
        code,
        orderAmount,
        storeId,
      });

      if (response.isValid) {
        onApply(code, response.discountAmount);
        setCouponCodeInput("");
        onOpenChange(false);
        toast({
          title: "Coupon applied successfully!",
          description: `You saved ₹${response.discountAmount}.`,
        });
      } else {
        setCouponError(response.message || "Invalid coupon code");
        toast({
          title: "Invalid Coupon",
          description: response.message || "Could not apply coupon.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setCouponError("Failed to validate coupon");
      toast({
        title: "Validation Failed",
        description: "An error occurred while validating the coupon.",
        variant: "destructive",
      });
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Apply Coupon</DialogTitle>
        </DialogHeader>

        {/* Manual Entry */}
        <div className="space-y-6 pt-2">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Enter Promo Code</Label>
            <div className="flex gap-2">
              <Input
                placeholder="E.G. WELCOME50"
                value={couponCodeInput}
                onChange={(e) => setCouponCodeInput(e.target.value)}
                className="h-10 text-sm font-mono uppercase max-w-sm"
              />
              <Button
                variant="default"
                onClick={() => handleApplyCoupon()}
                disabled={isValidatingCoupon || !couponCodeInput.trim()}
                className="h-10 shrink-0 font-semibold px-6"
              >
                {isValidatingCoupon ? "Applying..." : "Apply"}
              </Button>
            </div>
            {couponError && <p className="text-xs text-destructive font-medium">{couponError}</p>}
          </div>

          <Separator className="my-2" />

          {/* List of wallet coupons */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Your Wallet Coupons</h4>
            {isLoadingCoupons ? (
              <div className="space-y-2">
                <Skeleton className="h-16 rounded-xl" />
                <Skeleton className="h-16 rounded-xl" />
              </div>
            ) : userCoupons.filter((c) => c.status === 0).length > 0 ? (
              <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1.5 scrollbar-thin">
                {userCoupons.filter((c) => c.status === 0).map((coupon) => (
                  <div
                    key={coupon.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border bg-card hover:bg-secondary/15 transition-all shadow-xs"
                  >
                    <div>
                      <p className="text-sm font-bold font-mono text-foreground">{coupon.code}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">₹{coupon.discountValue} discount on your order</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApplyCoupon(coupon.code)}
                      className="text-xs font-semibold border-primary/20 text-primary hover:bg-primary hover:text-white px-4"
                    >
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">No personal wallet coupons available.</p>
            )}
          </div>

          {/* List of general platform coupons */}
          <div className="space-y-3 pt-2">
            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Store & Platform Offers</h4>
            {isLoadingPlatform ? (
              <div className="space-y-2">
                <Skeleton className="h-16 rounded-xl" />
                <Skeleton className="h-16 rounded-xl" />
              </div>
            ) : platformCoupons.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1.5 scrollbar-thin">
                {platformCoupons.map((coupon) => (
                  <div
                    key={coupon.id}
                    className="flex items-start justify-between p-3 rounded-xl border border-border bg-card hover:bg-secondary/15 transition-all shadow-xs gap-3"
                  >
                    <div className="min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold font-mono text-primary bg-primary/5 px-2.5 py-0.5 rounded-lg border border-primary/10 tracking-wide">{coupon.code}</span>
                        {coupon.minOrderAmount > 0 && (
                          <span className="text-[10px] text-muted-foreground">Min order ₹{coupon.minOrderAmount}</span>
                        )}
                      </div>
                      <p className="text-xs font-bold text-foreground mt-2">{coupon.name}</p>
                      {coupon.description && (
                        <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{coupon.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <span className="font-extrabold text-xs text-emerald-600 dark:text-emerald-400">
                        {coupon.type === 0 ? `${coupon.discountValue}% OFF` : `₹${coupon.discountValue} OFF`}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleApplyCoupon(coupon.code)}
                        className="text-xs font-semibold border-primary/20 text-primary hover:bg-primary hover:text-white px-4"
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">No promotional offers available at the moment.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
